import {
  BLASTCompAdjustment,
  BLASTGapCost,
  BLASTMatchScore,
  BLASTMatrix,
  BLASTStrand,
} from 'search/SearchConstants';

export interface PrimitiveBLASTParameter<ValueType> {
  defaultValue: ValueType;
}

export interface MultiChoiceBLASTParameter<ValueType> {
  // This can be a "local" default value, not necessarily a
  // "global" one. That is, the default value can be based
  // on the current values of other parameters, and it will
  // change accordingly. This is because BLAST parameters
  // can be interconnected. For example, based on the matrix,
  // the gap cost values and the default one will change.
  defaultValue: ValueType;
  values: Array<ValueType>;
}

export const isMultiChoiceBLASTParameter = <ValueType>(
  param: PrimitiveBLASTParameter<ValueType> | MultiChoiceBLASTParameter<ValueType>
): param is MultiChoiceBLASTParameter<ValueType> =>
  (param as MultiChoiceBLASTParameter<ValueType>).values != null;

export interface AllCandidateBLASTUiParamsType<BLASTType> {
  task: MultiChoiceBLASTParameter<BLASTType>;
  evalue: PrimitiveBLASTParameter<number>;
  culling_limit: PrimitiveBLASTParameter<number>;
  word_size: MultiChoiceBLASTParameter<number>;
  gap_cost: MultiChoiceBLASTParameter<BLASTGapCost>;
  match_score: MultiChoiceBLASTParameter<BLASTMatchScore>;
  strand: MultiChoiceBLASTParameter<BLASTStrand>;
  matrix: MultiChoiceBLASTParameter<BLASTMatrix>;
  comp_based_stats: MultiChoiceBLASTParameter<BLASTCompAdjustment>;
}

type UnwrapBLASTParameter<T> =
  T extends PrimitiveBLASTParameter<infer U> ? U : T extends MultiChoiceBLASTParameter<infer U> ? U : never;

export type UnwrappedBLASTParamsType<T> = {
  [K in keyof T]: UnwrapBLASTParameter<T[K]>;
};

export const MatrixToGapCosts: Record<BLASTMatrix, MultiChoiceBLASTParameter<BLASTGapCost>> = {
  [BLASTMatrix.PAM30]: {
    values: [
      {existence: 8, extension: 1},
      {existence: 9, extension: 1},
      {existence: 10, extension: 1},
      {existence: 14, extension: 1},

      {existence: 5, extension: 2},
      {existence: 6, extension: 2},
      {existence: 7, extension: 2},
      {existence: 14, extension: 2},

      {existence: 13, extension: 3},
      {existence: 15, extension: 3},
    ],
    defaultValue: {existence: 9, extension: 1},
  },
  [BLASTMatrix.PAM70]: {
    values: [
      {existence: 9, extension: 1},
      {existence: 10, extension: 1},
      {existence: 11, extension: 1},

      {existence: 6, extension: 2},
      {existence: 7, extension: 2},
      {existence: 8, extension: 2},
      {existence: 11, extension: 2},

      {existence: 12, extension: 3},
    ],
    defaultValue: {existence: 10, extension: 1},
  },
  [BLASTMatrix.PAM250]: {
    values: [
      {existence: 17, extension: 1},
      {existence: 18, extension: 1},
      {existence: 19, extension: 1},
      {existence: 20, extension: 1},
      {existence: 21, extension: 1},

      {existence: 13, extension: 2},
      {existence: 14, extension: 2},
      {existence: 15, extension: 2},
      {existence: 16, extension: 2},
      {existence: 17, extension: 2},

      {existence: 11, extension: 3},
      {existence: 12, extension: 3},
      {existence: 13, extension: 3},
      {existence: 14, extension: 3},
      {existence: 15, extension: 3},
    ],
    defaultValue: {existence: 14, extension: 2},
  },
  [BLASTMatrix.BLOSUM45]: {
    values: [
      {existence: 16, extension: 1},
      {existence: 17, extension: 1},
      {existence: 18, extension: 1},
      {existence: 19, extension: 1},

      {existence: 12, extension: 2},
      {existence: 13, extension: 2},
      {existence: 14, extension: 2},
      {existence: 15, extension: 2},

      {existence: 10, extension: 3},
      {existence: 11, extension: 3},
      {existence: 12, extension: 3},
      {existence: 13, extension: 3},
    ],
    defaultValue: {existence: 15, extension: 2},
  },
  [BLASTMatrix.BLOSUM50]: {
    values: [
      {existence: 15, extension: 1},
      {existence: 16, extension: 1},
      {existence: 17, extension: 1},
      {existence: 18, extension: 1},
      {existence: 19, extension: 1},

      {existence: 12, extension: 2},
      {existence: 13, extension: 2},
      {existence: 14, extension: 2},
      {existence: 15, extension: 2},
      {existence: 16, extension: 2},

      {existence: 9, extension: 3},
      {existence: 10, extension: 3},
      {existence: 11, extension: 3},
      {existence: 12, extension: 3},
      {existence: 13, extension: 3},
    ],
    defaultValue: {existence: 13, extension: 2},
  },
  [BLASTMatrix.BLOSUM62]: {
    values: [
      {existence: 9, extension: 1},
      {existence: 10, extension: 1},
      {existence: 11, extension: 1},
      {existence: 12, extension: 1},
      {existence: 13, extension: 1},

      {existence: 6, extension: 2},
      {existence: 7, extension: 2},
      {existence: 8, extension: 2},
      {existence: 9, extension: 2},
      {existence: 10, extension: 2},
      {existence: 11, extension: 2},
    ],
    defaultValue: {existence: 11, extension: 1},
  },
  [BLASTMatrix.BLOSUM80]: {
    values: [
      {existence: 9, extension: 1},
      {existence: 10, extension: 1},
      {existence: 11, extension: 1},

      {existence: 6, extension: 2},
      {existence: 7, extension: 2},
      {existence: 8, extension: 2},
    ],
    defaultValue: {existence: 10, extension: 1},
  },
  [BLASTMatrix.BLOSUM90]: {
    values: [
      {existence: 9, extension: 1},
      {existence: 10, extension: 1},
      {existence: 11, extension: 1},

      {existence: 6, extension: 2},
      {existence: 7, extension: 2},
      {existence: 8, extension: 2},
      {existence: 9, extension: 2},
    ],
    defaultValue: {existence: 10, extension: 1},
  },
};

export const CommonBLASTParamValues: Pick<
  AllCandidateBLASTUiParamsType<never>,
  'evalue' | 'culling_limit' | 'gap_cost' | 'strand' | 'matrix' | 'comp_based_stats'
> = {
  matrix: {defaultValue: BLASTMatrix.BLOSUM62, values: Object.values(BLASTMatrix)},
  evalue: {defaultValue: 10},
  culling_limit: {defaultValue: 0},
  gap_cost: MatrixToGapCosts[BLASTMatrix.BLOSUM62],
  strand: {defaultValue: BLASTStrand.BOTH, values: Object.values(BLASTStrand)},
  comp_based_stats: {
    defaultValue: BLASTCompAdjustment.ConditionalAdjustment,
    values: Object.values(BLASTCompAdjustment),
  },
};
